// Surface colours for anything that paints a surface type — the planner's
// elevation profile line, its tooltip swatch, and (via the Ruby mirror below)
// the hike overview "Surfaces" card.
//
// This palette and `HikesHelper::SURFACE_COLOR_MAP` are the same palette
// rendered by two stacks, so they have to agree: asphalt used to be a dark
// grey here and a blue on the overview, which made the same stretch of route
// look like two different surfaces depending on which page you were on.
// `spec/helpers/hikes_helper_spec.rb` fails the build if the two drift again.
//
// Keys are the camelCase names the planner uses (see
// `LineStringPropertiesSurface#surfaceIntToString`); `surfaceColor` also
// accepts the snake_case names the Rails side uses.
export const SURFACE_COLORS: Record<string, string> = {
  unknown:         '#f4f4f5',
  paved:           '#5caee3',
  unpaved:         '#F57700', // var(--orange)
  asphalt:         '#4a90e2',
  concrete:        '#9ca3af',
  cobblestone:     '#b7955b',
  metal:           '#7d8793',
  wood:            '#c68a4d',
  compactedGravel: '#7a6d52',
  fineGravel:      '#a89b7b',
  gravel:          '#5f5748',
  dirt:            '#8b5a2b',
  ground:          '#a8734c',
  ice:             '#cfe8ff',
  pavingStones:    '#6eced9',
  sand:            '#f2d28b',
  woodchips:       '#ba7d43',
  grass:           '#4BC263', // var(--green)
  grassPaver:      '#7ccb86',
};

export const UNKNOWN_SURFACE_COLOR = SURFACE_COLORS.unknown;

const toCamelCase = (key: string): string =>
  key.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());

// Colour for a surface name, in either camelCase ('compactedGravel') or
// snake_case ('compacted_gravel'). Anything unrecognised — including the
// planner's 'missing' placeholder and a surface type the backend starts
// sending before the frontend knows about it — falls back to the "unknown"
// colour, which is what those stretches are.
export const surfaceColor = (surface?: string | null): string => {
  if (!surface) return UNKNOWN_SURFACE_COLOR;

  return SURFACE_COLORS[surface] ?? SURFACE_COLORS[toCamelCase(surface)] ?? UNKNOWN_SURFACE_COLOR;
};
